import { useMemo } from 'react';
import {
  AllAccessLocationFilterSelector,
  FilterTraySection,
  useAnalyticsOrgLocations,
} from '@frontend/analytics-shared';
import { GroupedCheckListDropdownMenu } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useIsAppointmentDemoAccount } from '../../hooks/use-is-appointment-demo-account';

interface AppointmentLocationFilterProps {
  value?: string[];
  onChange: (locationId: string[]) => void;
  isTrayFilter?: boolean;
}

export const AppointmentLocationFilter = ({ value, onChange, isTrayFilter }: AppointmentLocationFilterProps) => {
  const { t } = useTranslation('analytics');
  const isDemoAccount = useIsAppointmentDemoAccount();

  const { hasAccessToMultipleLocations, locationNames, preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'APPOINTMENT',
  });

  const locationGroups = useMemo(() => {
    return Object.entries(locationNames || {}).map(([id, name]) => ({
      children: [],
      label: name,
      value: id,
    }));
  }, [locationNames]);

  if (!hasAccessToMultipleLocations) {
    return null;
  }

  if (isTrayFilter) {
    return (
      <FilterTraySection
        label={t('Location')}
        onReset={() => onChange(preselectedAccessibleLocationIds)}
        disableReset={
          !value ||
          (value.length === preselectedAccessibleLocationIds.length &&
            value.every((id) => preselectedAccessibleLocationIds.includes(id)))
        }
      >
        <GroupedCheckListDropdownMenu
          groups={locationGroups}
          label={t('Location')}
          name='appointment-location-filter'
          onChange={(locationId) => {
            onChange(!locationId.length ? preselectedAccessibleLocationIds : locationId);
          }}
          placeholder={t('Select Locations')}
          value={value || preselectedAccessibleLocationIds}
        />
      </FilterTraySection>
    );
  }

  return (
    <AllAccessLocationFilterSelector
      locations={locationNames}
      onChange={(locationId) => {
        onChange(!locationId.length ? preselectedAccessibleLocationIds : locationId);
      }}
      value={value}
      defaultValue={preselectedAccessibleLocationIds}
      renderIcon={(isActive) => <Icon name='location-small' color={isActive ? 'white' : 'default'} />}
    />
  );
};
